import URLUtils from '@/shared/utils/URLUtils';
import { MembersRightsPrefix } from '@/constant';
import { MemberRouterNameEnum } from '@/shared/right-utils/routerName';
import {
  RightPackageSourceEnum,
  RightPackageTypeEnum,
  RightSummaryType,
  LoanCardTypeEnum,
} from '@/api/rights/types';
import { ApplyChannelData } from '@/api/apply-product/types';
import { ubtSendError } from '@/shared/utils/ubt';

/**
 * 路由配置映射表
 * 根据 LoanCardTypeEnum 枚举值配置对应的路由名称
 */
const LOAN_CARD_ROUTE_MAP = {
  [LoanCardTypeEnum.CHANNEL_B]: MemberRouterNameEnum.ZHENXIANG_CARD_API,
  [LoanCardTypeEnum.JOINT_VENTURE]: MemberRouterNameEnum.ZHENXIANG_CARD_API,
  [LoanCardTypeEnum.OUT_FLOW]: MemberRouterNameEnum.ZHENXIANG_CARD_API,
  [LoanCardTypeEnum.YOU_SU_CARD]: MemberRouterNameEnum.ZHENXIANG_CARD_API,
  [LoanCardTypeEnum.CHANNEL_RIGHT]: null, // 使用 unopenedDetailUrl
} as const;

/**
 * 构建权益详情页URL的参数接口
 */
interface GetDetailUrlParams {
  /** 渠道信息 */
  channelInfo?: ApplyChannelData;
  /** 权益参数 */
  rightParams: RightSummaryType;
  /** 来源 */
  from?: RightPackageSourceEnum;
}

/**
 * 构建基础URL参数
 */
interface BaseUrlParams {
  packageId: string;
  capitalCode?: string;
  sourceId?: string;
  from: RightPackageSourceEnum;
  hiddenPayButton: boolean;
  rightsModelType?: number;
  loanCardType?: LoanCardTypeEnum;
  [key: string]: unknown;
}

/**
 * 构建基础URL参数
 */
const buildBaseUrlParams = (
  channelInfo: ApplyChannelData | undefined,
  rightParams: RightSummaryType,
  from?: RightPackageSourceEnum,
): BaseUrlParams => {
  const { capitalCode, sourceId } = channelInfo || {};
  const { packageId, rightsModelType, loanCardType } = rightParams;

  return {
    packageId,
    capitalCode,
    sourceId,
    from: RightPackageSourceEnum.LOAN || from,
    hiddenPayButton: true,
    rightsModelType,
    loanCardType,
  };
};

/**
 * 构建完整的权益详情页URL
 */
const buildDetailUrl = (
  routerName: MemberRouterNameEnum,
  params: BaseUrlParams,
): string => URLUtils.formatUrl(
  `${MembersRightsPrefix}${routerName}.html#/${routerName}`,
  params,
);

/**
 * 检查是否应该使用臻享会员卡路由
 */
const shouldUseZhenXiangMemberCard = (
  packageType?: RightPackageTypeEnum,
): boolean => packageType === RightPackageTypeEnum.ZHEN_XIANG_MEMBER_CARD;

/**
 * 根据贷款卡类型获取权益详情页URL
 *
 * 路由规则：
 * - 自营(1)、联营(2): 跳转 ZHENXIANG_CARD
 * - 外采(3)、轻资产优速卡(4): 跳转 ZHENXIANG_CARD_API
 * - 渠道权益页(5): 使用 unopenedDetailUrl
 * - 特殊情况: packageType 为 ZHEN_XIANG_MEMBER_CARD 时跳转 ZHENXIANG_MEMBER_CARD
 *
 * @param params 构建URL所需的参数
 * @returns 权益详情页URL
 */
export const getDetailUrl = (params: GetDetailUrlParams): string => {
  const { channelInfo, rightParams, from } = params;
  const { unopenedDetailUrl, packageType, loanCardType } = rightParams;

  // 构建基础URL参数
  const baseParams = buildBaseUrlParams(channelInfo, rightParams, from);

  // 特殊情况：臻享会员卡优先级最高
  if (shouldUseZhenXiangMemberCard(packageType)) {
    return buildDetailUrl(MemberRouterNameEnum.ZHENXIANG_MEMBER_CARD, baseParams);
  }

  // 如果没有 loanCardType，返回空字符串
  if (!loanCardType) {
    ubtSendError('loanCardType is undefined');
    return '';
  }

  let routerName: MemberRouterNameEnum | null = null;
  // 渠道权益页类型，使用未开通详情页URL
  if (loanCardType === LoanCardTypeEnum.CHANNEL_RIGHT) {
    if (!unopenedDetailUrl) {
      ubtSendError('unopenedDetailUrl is undefined');
      return '';
    }
    return unopenedDetailUrl;
  }
  // 根据贷款卡类型获取对应的路由名称
  routerName = LOAN_CARD_ROUTE_MAP[loanCardType];

  // 如果找不到对应的路由配置，返回空字符串
  if (!routerName) {
    ubtSendError('detail url not found');
    return '';
  }

  return buildDetailUrl(routerName, baseParams);
};
